// src/pages/HomePage.jsx
// Accueil « Le Brief » — premier écran basculé sur le socle « Le Cabinet ».
// Réf. visuelle EXACTE : design-mockups/concepts-v2/le-cabinet/index.html (onglet « Le Brief »).
//
// L'app n'est pas un tableau de bord : c'est le cabinet qui travaille pour vous.
// On sert des DÉCISIONS, pas des données. Page recentrée (juin 2026) sur sa
// mission après dégraissage — DEUX blocs seulement :
//   ① Agir maintenant : Débrief de Claire (synthèse + « confier une tâche »)
//      puis Décisions à arbitrer (réponses préparées à valider en 1 clic).
//   ② En mouvement : Cabinet vivant (qui travaille sur quoi, en direct) +
//      Échéances (agenda réel).
// Retirés au dégraissage : bande « Pendant votre nuit » (stats redondantes),
// « Vos affaires » et « Vos collaborateurs » (navigation pure → barre du haut).
//
// DONNÉES RÉELLES (rien d'inventé). Props PRÉSERVÉS :
//   { lawyer, stats, msgs, dossiers, setPage, setPendingSkill, setPendingContext, isMobile }
// Composant de données réutilisé tel quel : AgendaUpcoming (échéances).
// Les mails préparés (décisions) et les jobs (cabinet vivant) sont requêtés ici
// via Supabase — logique héritée des widgets de l'ancienne homepage.
//
// SOCLE : on consomme les tokens `cab`/`CAB_MATIERE`/`cabMatiere` (var(--cab-*))
// et les fonts Fraunces/Geist Mono (fonts.serifDisplay / fonts.monoCab).
// La page peint sa propre surface ivoire localement — elle ne touche PAS aux
// variables « E » globales, donc les autres écrans (encore en E) restent intacts.

import { useState, useEffect, useRef } from 'react';
import { supabase } from '../lib/supabase';
import { useIsMobile } from '../hooks/useIsMobile';
import { cab as K, fonts as F, cabMatiere } from '../theme';
import { AGENTS } from '../data/agents';
import { agentForSlug, skillTaskLabel, humanizeSlug, fmtWhen, countPendingDecisions, countWorkingJobs, validateSkillJob } from '../components/cab/cabHelpers';
import CabFooterUE from '../components/cab/CabFooterUE';
import { agentForMatiere, matiereFromTitle } from '../data/matiere-to-agent';
import AgendaUpcoming from '../components/dashboard/AgendaUpcoming';

const CLAIRE_PHOTO = AGENTS.find(a => a.id === 'claire')?.photoUrl
  || 'https://mhixfakpafenionfrpto.supabase.co/storage/v1/object/public/agent-photos/claire.jpg';

// Sophie (secrétaire) — entrée CANONIQUE issue d'AGENTS pour router une
// décision-mail vers le chat. On part du référentiel (qui porte `photoUrl`,
// `bio`, etc.) : sans lui, `activeAgent` arrivait sur ChatPage sans photoUrl →
// avatar cassé dans le chat ouvert depuis un courriel. Fallback inline défensif
// (avec photoUrl) si l'entrée venait à disparaître du référentiel.
const SOPHIE_AGENT = AGENTS.find((a) => a.id === 'sophie') || {
  id: 'sophie', name: 'Sophie', title: 'Secrétaire juridique',
  gender: 'F', category: 'secretariat',
  skills: ['secretaire-emails', 'secretaire-agenda', 'secretaire-courriers', 'secretaire-cabinet'],
  primarySkill: 'secretaire-emails',
  photoUrl: 'https://mhixfakpafenionfrpto.supabase.co/storage/v1/object/public/agent-photos/sophie.jpg',
};

// ─── Icônes (stroke currentColor, héritent de la couleur du parent) ───
const Ic = {
  send: (p) => (<svg viewBox="0 0 24 24" fill="none" {...p}><path d="M5 12h14M13 6l6 6-6 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" /></svg>),
  chevron: (p) => (<svg viewBox="0 0 24 24" fill="none" {...p}><path d="M9 6l6 6-6 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" /></svg>),
  chat: (p) => (<svg viewBox="0 0 24 24" fill="none" {...p}><path d="M4 5h16v11H8l-4 4z" stroke="currentColor" strokeWidth="1.7" strokeLinejoin="round" /></svg>),
  doc: (p) => (<svg viewBox="0 0 24 24" fill="none" {...p}><path d="M4 19V5a2 2 0 012-2h9l5 5v11a2 2 0 01-2 2H6a2 2 0 01-2-2z" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round" /></svg>),
  clock: (p) => (<svg viewBox="0 0 24 24" fill="none" {...p}><circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.7" /><path d="M12 8v4l3 2" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" /></svg>),
  cal: (p) => (<svg viewBox="0 0 24 24" fill="none" {...p}><rect x="3" y="5" width="18" height="16" rx="3" stroke="currentColor" strokeWidth="1.7" /><path d="M3 9h18M8 3v4M16 3v4" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" /></svg>),
  alert: (p) => (<svg viewBox="0 0 24 24" fill="none" {...p}><path d="M12 9v4m0 4h.01M10.3 3.9l-8 13.8A2 2 0 004 21h16a2 2 0 001.7-3.3l-8-13.8a2 2 0 00-3.4 0z" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" /></svg>),
  check: (p) => (<svg viewBox="0 0 24 24" fill="none" {...p}><path d="M5 13l4 4L19 7" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" /></svg>),
  team: (p) => (<svg viewBox="0 0 24 24" fill="none" {...p}><circle cx="9" cy="8" r="3.2" stroke="currentColor" strokeWidth="1.7" /><circle cx="17" cy="9" r="2.6" stroke="currentColor" strokeWidth="1.6" /><path d="M3.5 19c0-3 2.5-5 5.5-5s5.5 2 5.5 5M15 19c0-2 1-3.4 2.8-3.8" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" /></svg>),
  pulse: (p) => (<svg viewBox="0 0 24 24" fill="none" {...p}><path d="M3 13h3l2-6 4 12 2.5-7 1.5 3H21" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" /></svg>),
};

export default function HomePage({ lawyer, stats, msgs, dossiers, setPage, setPendingSkill, setPendingContext, isMobile }) {
  const lawyerId = lawyer?.id;
  // Horloge vivante : rafraîchie chaque minute, pour que l'heure du débrief et
  // le salut (Bonjour/Bonsoir) restent justes si l'onglet reste ouvert.
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 60000);
    return () => clearInterval(id);
  }, []);

  // Signal de refresh commun hero ↔ tuile Décisions : chaque validation 1-clic
  // réussie incrémente la version → le débrief de Claire recompte (sinon son
  // chiffre reste figé jusqu'au reload) et la tuile recharge ses jobs (la
  // décision suivante remonte quand une des cartes affichées est validée).
  const [decisionsVersion, setDecisionsVersion] = useState(0);
  const bumpDecisions = () => setDecisionsVersion(v => v + 1);

  // ── Handlers (PRÉSERVÉS depuis l'ancienne HomePage) ──
  const handleAskClaire = (e) => {
    e?.preventDefault?.();
    setPendingSkill(null); setPendingContext(null); setPage('chat');
  };

  const handleSelectEmail = (email) => {
    setPendingSkill(SOPHIE_AGENT);
    setPendingContext({
      type: 'email',
      emailId: email.id,
      from: email.from_name || email.from_email,
      subject: email.subject,
      body: (email.body_text || '').substring(0, 2000),
    });
    setPage('chat');
  };

  // Anticipation des audiences (agent_proposals du cron scan-audiences) :
  // l'avocat accepte depuis le débrief → le travail part dans le chat avec le
  // collaborateur référent, contexte identique au « Confier » de l'Agenda.
  const handlePrepareProposal = (p) => {
    const pl = p?.action_payload || {};
    const agent = referentForProposal(p);
    const d = pl.event_start_at ? new Date(pl.event_start_at) : null;
    const dateStr = d ? d.toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' }) : '';
    const timeStr = d ? d.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' }) : '';
    const matiereKey = pl.matiere || matiereFromTitle(pl.event_title || '') || null;
    const contextLines = [
      `Contexte — événement agenda :`,
      `• Titre : ${pl.event_title || p?.title || '(sans titre)'}`,
      dateStr ? `• Date : ${dateStr}${timeStr ? ' à ' + timeStr : ''}` : '',
      pl.event_location ? `• Lieu : ${pl.event_location}` : '',
      pl.client_name
        ? `• Dossier : ${pl.client_name}${pl.dossier_ref ? ' (' + pl.dossier_ref + ')' : ''}${matiereKey ? ' — ' + matiereKey : ''}`
        : '• Dossier : non rattaché',
      '',
      `Ma demande : ${(pl.suggested_actions || []).join('. ') || "Merci de m'aider à préparer cet événement."}`,
    ].filter(Boolean).join('\n');
    if (agent) setPendingSkill(agent);
    setPendingContext({
      type: 'calendar',
      event_id: pl.calendar_event_id || null,
      dossier_id: pl.dossier_id || null,
      prompt: contextLines,
    });
    setPage('chat');
  };

  // ── Données dérivées (aucune valeur inventée) ──
  const lastName = lawyer?.last_name || lawyer?.first_name || '';
  const dateStr = now.toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' });
  const dateCap = dateStr.charAt(0).toUpperCase() + dateStr.slice(1);
  const timeStr = now.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
  // Salut adapté à l'heure : « Bonsoir » dès 18h et avant 5h, « Bonjour » sinon.
  const hour = now.getHours();
  const greeting = (hour < 5 || hour >= 18) ? 'Bonsoir' : 'Bonjour';

  const unreadCount = msgs?.filter?.(m => !m.is_read)?.length || 0;
  const dossiersCount = dossiers?.length || 0;

  // ── Fragments réutilisables — MÊMES sous-composants, réagencés selon `variant`.
  //    (Prototypage de l'organisation de la page : current / light / moderate / strong.
  //    À collapser sur la variante retenue une fois le choix fait.) ──
  const heroEl = (
    <ClaireDebrief
      dateCap={dateCap} timeStr={timeStr} lastName={lastName} greeting={greeting}
      lawyerId={lawyerId} dossiersCount={dossiersCount} unreadCount={unreadCount}
      refreshKey={decisionsVersion}
      onAsk={handleAskClaire} onPrepareProposal={handlePrepareProposal} isMobile={isMobile}
    />
  );
  const decisionsEl = <DecisionsTile lawyerId={lawyerId} refreshKey={decisionsVersion} onValidated={bumpDecisions} onSelectEmail={handleSelectEmail} onSeeAll={() => setPage('emails')} />;
  const cabinetEl = <CabinetVivantTile lawyerId={lawyerId} />;
  const echeancesEl = <EcheancesTile lawyerId={lawyerId} onSeeAll={() => setPage('agenda')} />;
  const footerEl = <CabFooterUE style={{ marginTop: isMobile ? 28 : 38 }} />;

  // Bento deux colonnes (gauche large / droite étroite). rightPct règle la largeur droite.
  const bento = (left, right, rightPct = '40%') => (
    <div style={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', gap: isMobile ? 16 : 18, marginTop: isMobile ? 22 : 26, alignItems: 'flex-start' }}>
      <div style={{ flex: isMobile ? 'none' : '1 1 0', width: isMobile ? '100%' : 'auto', minWidth: 0 }}>{left}</div>
      <div style={{ flex: isMobile ? 'none' : `0 0 ${rightPct}`, width: isMobile ? '100%' : 'auto', minWidth: 0, display: 'flex', flexDirection: 'column', gap: isMobile ? 16 : 18 }}>{right}</div>
    </div>
  );

  // Étiquette de bloc (variantes « moderate » / « strong »).
  const blocLabel = (txt) => (
    <div style={{ fontFamily: F.monoCab, fontSize: 11, letterSpacing: '.18em', textTransform: 'uppercase', color: K.ink3, margin: isMobile ? '26px 0 14px' : '34px 0 16px', display: 'flex', alignItems: 'center', gap: 12 }}>
      <span aria-hidden="true" style={{ width: 22, height: 1, background: K.bordeaux }} />{txt}
    </div>
  );

  // Accueil recentré sur sa mission : « ce qui me réclame » (Hero + Décisions)
  // puis « ce que le cabinet fait » (Cabinet vivant + Échéances). La navigation
  // pure (dossiers / équipe) vit dans la barre du haut, pas ici.
  const content = (<>
    {heroEl}
    <div style={{ marginTop: isMobile ? 20 : 24 }}>{decisionsEl}</div>
    {blocLabel('En mouvement')}
    {bento(cabinetEl, echeancesEl, '50%')}
  </>);

  return (
    <div style={{ fontFamily: F.sans, color: K.ink, paddingTop: isMobile ? 8 : 16 }}>
      {/* Surface ivoire « Le Cabinet » — peinte localement (ne touche pas --bg-page). */}
      <div style={{
        background: `radial-gradient(120% 80% at 100% 0%, rgba(122,31,46,.035), transparent 60%), ${K.paper}`,
        borderRadius: K.rXl,
        border: `1px solid ${K.hair}`,
        padding: isMobile ? '20px 16px 28px' : '32px 34px 40px',
        boxShadow: K.lift1,
      }}>
        {content}
        {footerEl}
      </div>
    </div>
  );
}

/* ════════════════════════════════════════════════════════════════════════
   1. DÉBRIEF DE CLAIRE — bandeau d'accueil sombre « console »
   La synthèse est dérivée des comptes réels (jobs préparés/en cours, échéances).
   ════════════════════════════════════════════════════════════════════════ */
function ClaireDebrief({ dateCap, timeStr, lastName, greeting, lawyerId, dossiersCount, unreadCount, refreshKey, onAsk, onPrepareProposal, isMobile }) {
  const [prepared, setPrepared] = useState(null);   // jobs terminés en attente (≈ travail prêt)
  const [running, setRunning] = useState(null);     // jobs en cours
  const [audienceProposals, setAudienceProposals] = useState([]); // anticipation audiences (cron scan-audiences)

  useEffect(() => {
    if (!lawyerId) { setPrepared(0); setRunning(0); return; }
    // MÊME population que la page Dossiers (sémantique partagée cabHelpers),
    // mais comptée EXACTEMENT côté base (head:true, count:'exact') : l'ancien
    // .select().limit(100) compté côté client plafonnait le chiffre à 100 sur
    // les cabinets chargés. `refreshKey` fait recompter après chaque
    // validation 1-clic dans la tuile Décisions juste en dessous.
    let active = true;
    Promise.all([countPendingDecisions(lawyerId), countWorkingJobs(lawyerId)])
      .then(([p, r]) => { if (active) { setPrepared(p ?? 0); setRunning(r ?? 0); } })
      .catch(() => { if (active) { setPrepared(0); setRunning(0); } });
    return () => { active = false; };
  }, [lawyerId, refreshKey]);

  // 1-2 propositions d'anticipation des audiences (même flux que le bandeau
  // Agenda — RLS lawyers_own_proposals). Les plus proches d'abord.
  useEffect(() => {
    if (!lawyerId) return;
    supabase
      .from('agent_proposals')
      .select('id, title, status, action_payload, expires_at')
      .eq('lawyer_id', lawyerId)
      .eq('proposal_type', 'brief_audience')
      .eq('source_workflow', 'cron-scan-audiences')
      .eq('status', 'pending')
      .order('expires_at', { ascending: true })
      .limit(2)
      .then(({ data }) => setAudienceProposals(data || []))
      .then(undefined, () => setAudienceProposals([]));
  }, [lawyerId]);

  // Réponse de l'avocat. Accepter → executed (le chat part immédiatement avec
  // le référent) ; ignorer → rejected. Rien ne se lance sans ce clic.
  const respondAudienceProposal = async (p, accept) => {
    setAudienceProposals(prev => (prev || []).filter(x => x.id !== p.id));
    const nowIso = new Date().toISOString();
    const patch = accept
      ? { status: 'executed', responded_at: nowIso, executed_at: nowIso }
      : { status: 'rejected', responded_at: nowIso };
    try {
      const { error } = await supabase.from('agent_proposals').update(patch).eq('id', p.id);
      if (error) console.error('[ClaireDebrief] agent_proposals update:', error.message);
    } catch { /* silent */ }
    if (accept) onPrepareProposal?.(p);
  };

  // Synthèse honnête : « réponses préparées » = mails non lus prêts à traiter +
  // jobs terminés ; « décisions » = ce qui attend l'avocat (≈ réponses préparées).
  const preparedReplies = (unreadCount || 0) + (prepared || 0);
  const decisions = preparedReplies;
  const hasActivity = decisions > 0 || (running || 0) > 0;

  return (
    <section>
      <div style={{
        fontFamily: F.monoCab, fontSize: 11.5, letterSpacing: '.22em', textTransform: 'uppercase',
        color: K.ink3, marginBottom: 16, display: 'flex', alignItems: 'center', gap: 12,
      }}>
        <span aria-hidden="true" style={{ width: 26, height: 1, background: K.bordeaux }} />
        Votre cabinet · {dateCap} · {timeStr}
      </div>

      <div style={{
        position: 'relative', borderRadius: K.rXl, overflow: 'hidden', color: '#F6ECEE',
        background: 'radial-gradient(125% 130% at 6% 0%, rgba(168,50,74,.40), transparent 56%), radial-gradient(90% 120% at 100% 100%, rgba(46,114,184,.12), transparent 60%), linear-gradient(160deg, #3a1620, #271017)',
        boxShadow: '0 30px 70px -34px rgba(78,22,32,.85)',
      }}>
        <div style={{
          padding: isMobile ? '22px 20px' : '30px 34px 28px',
          display: 'flex', gap: isMobile ? 18 : 26, alignItems: 'flex-start', flexWrap: 'wrap',
        }}>
          {/* Visage de Claire */}
          <span style={{ position: 'relative', width: 74, height: 74, flex: 'none' }}>
            <span aria-hidden="true" style={{
              position: 'absolute', inset: -6, borderRadius: 25,
              border: '1.5px solid rgba(255,180,195,.7)', opacity: .6,
              animation: 'cabRing 3.4s var(--cab-ease-soft) infinite',
            }} />
            <img src={CLAIRE_PHOTO} alt="Claire, orchestratrice du cabinet" loading="lazy"
              style={{ width: '100%', height: '100%', borderRadius: 20, objectFit: 'cover', objectPosition: 'center top', border: '1.5px solid rgba(255,255,255,.28)', boxShadow: '0 10px 26px rgba(0,0,0,.5)' }} />
          </span>

          {/* Sur mobile, minWidth 100% force le passage SOUS l'avatar (sinon le
              contenu — titre, champ, proposition — s'écrase sur ~240px). */}
          <div style={{ flex: 1, minWidth: isMobile ? '100%' : 300 }}>
            <div style={{
              fontFamily: F.monoCab, fontSize: 10.5, letterSpacing: '.18em', textTransform: 'uppercase',
              color: 'rgba(255,190,202,.95)', marginBottom: 10, display: 'flex', alignItems: 'center', gap: 10,
            }}>
              <span aria-hidden="true" style={{
                width: 7, height: 7, borderRadius: '50%', background: '#5BE0A6',
                boxShadow: '0 0 0 3px rgba(91,224,166,.22)', animation: 'cabBlink 2.6s infinite',
              }} />
              Claire · Orchestratrice — débrief
            </div>

            <h1 style={{
              fontFamily: F.serifDisplay, fontWeight: 400, fontSize: isMobile ? 25 : 'clamp(25px,3.1vw,38px)',
              lineHeight: 1.16, letterSpacing: '-.02em', color: '#fff', maxWidth: '30ch', margin: 0,
            }}>
              {greeting} {lastName ? <>M<sup style={{ fontSize: '0.5em' }}>e</sup> {lastName}.</> : 'Maître.'}{' '}
              {hasActivity ? (
                <>Le cabinet a fait avancer vos dossiers
                  {preparedReplies > 0
                    ? <> et préparé <em style={{ fontStyle: 'italic', color: K.gildHi }}>{preparedReplies} {preparedReplies > 1 ? 'réponses' : 'réponse'}</em> — il ne reste {preparedReplies > 1 ? "qu'à les valider" : "qu'à la valider"}.</>
                    : <>. <b style={{ fontWeight: 600, color: '#fff' }}>Tout est en ordre.</b></>}
                </>
              ) : (
                <>Le cabinet veille sur vos {dossiersCount > 0 ? <em style={{ fontStyle: 'italic', color: K.gildHi }}>{dossiersCount} dossiers</em> : 'dossiers'}. Rien ne réclame votre arbitrage pour l'instant.</>
              )}
            </h1>

            <div style={{
              marginTop: 16, fontSize: 13.5, color: 'rgba(246,236,238,.7)',
              display: 'flex', alignItems: 'center', gap: 9, flexWrap: 'wrap',
            }}>
              <span style={{ fontFamily: F.serifDisplay, fontStyle: 'italic', color: K.gildHi, fontWeight: 500 }}>— Tout le reste tourne.</span>
              {hasActivity ? "Je vous ai mis l'essentiel en haut, prêt à trancher." : 'Confiez-moi ce dont vous avez besoin.'}
            </div>

            {/* Champ « confier une tâche au cabinet » → chat */}
            <form onSubmit={onAsk} style={{ marginTop: 20, position: 'relative', maxWidth: 520 }}>
              <input
                type="text" readOnly onFocus={onAsk} onClick={onAsk}
                placeholder="Confiez une tâche au cabinet…"
                aria-label="Confier une tâche à Claire"
                style={{
                  width: '100%', borderRadius: K.rMd, background: 'rgba(0,0,0,.26)',
                  border: '1px solid rgba(255,255,255,.16)',
                  padding: isMobile ? '13px 16px' : '14px 130px 14px 16px',
                  color: 'rgba(246,236,238,.6)', fontFamily: F.serifDisplay, fontSize: 14.5,
                  letterSpacing: '-.01em', cursor: 'pointer',
                }}
              />
              <button type="submit" aria-label="Confier la tâche" style={{
                position: 'absolute', right: 9, top: '50%', transform: 'translateY(-50%)',
                height: 38, padding: '0 15px', borderRadius: 11, border: 'none',
                background: '#fff', color: K.bordeaux, fontWeight: 700, fontSize: 13, cursor: 'pointer',
                display: isMobile ? 'none' : 'flex', alignItems: 'center', gap: 7,
                boxShadow: '0 6px 16px -6px rgba(0,0,0,.5)', fontFamily: F.sans,
              }}>
                Confier <Ic.send style={{ width: 15, height: 15 }} />
              </button>
            </form>

            {/* Anticipation des audiences — propositions du cron scan-audiences.
                L'avocat tranche : « Confier à {référent} » lance le chat,
                « Ignorer » rejette. Aucune action sans son accord. */}
            {(audienceProposals?.length || 0) > 0 && (
              <div style={{ marginTop: 18, display: 'flex', flexDirection: 'column', gap: 9, maxWidth: 560 }}>
                {audienceProposals.map(p => {
                  const pl = p.action_payload || {};
                  const agent = referentForProposal(p);
                  const d = pl.event_start_at ? new Date(pl.event_start_at) : null;
                  const diff = d ? Math.ceil((d - new Date()) / 86400000) : null;
                  const when = diff === null ? '' : diff <= 0 ? "aujourd'hui" : diff === 1 ? 'demain' : `dans ${diff} j`;
                  return (
                    <div key={p.id} style={{
                      display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap',
                      borderRadius: 13, padding: '11px 14px',
                      background: 'rgba(255,255,255,.07)', border: '1px solid rgba(255,255,255,.14)',
                    }}>
                      <span aria-hidden="true" style={{ color: K.gildHi, display: 'flex', width: 16, height: 16, flex: 'none' }}>
                        <Ic.cal style={{ width: 16, height: 16 }} />
                      </span>
                      <div style={{ flex: 1, minWidth: isMobile ? '100%' : 200 }}>
                        <div style={{ fontSize: 13, color: '#fff', fontWeight: 600, lineHeight: 1.35 }}>
                          {pl.category_label || 'Audience'} « {pl.event_title || p.title} »{when ? ` ${when}` : ''} — préparer ?
                        </div>
                        <div style={{ fontSize: 11.5, color: 'rgba(246,236,238,.7)', marginTop: 2 }}>
                          {(pl.suggested_actions || [])[0] || 'Travaux préparatoires'}{pl.client_name ? ` · ${pl.client_name}` : ''}
                        </div>
                      </div>
                      <div style={{ display: 'flex', gap: 8, flex: 'none', flexWrap: 'wrap' }}>
                        <button
                          onClick={() => respondAudienceProposal(p, true)}
                          aria-label={`Confier la préparation de ${pl.event_title || p.title}`}
                          style={{
                            height: 32, padding: '0 13px', borderRadius: 9, border: 'none', cursor: 'pointer',
                            fontFamily: F.sans, fontWeight: 700, fontSize: 12, color: K.bordeaux, background: '#fff',
                            boxShadow: '0 6px 16px -6px rgba(0,0,0,.5)',
                          }}
                        >
                          {agent ? `Confier à ${agent.name}` : 'Préparer'}
                        </button>
                        <button
                          onClick={() => respondAudienceProposal(p, false)}
                          aria-label={`Ignorer la proposition ${pl.event_title || p.title}`}
                          style={{
                            height: 32, padding: '0 11px', borderRadius: 9, cursor: 'pointer',
                            fontFamily: F.sans, fontWeight: 600, fontSize: 12,
                            color: 'rgba(246,236,238,.85)', background: 'transparent',
                            border: '1px solid rgba(255,255,255,.28)',
                          }}
                        >
                          Ignorer
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}

/* ════════════════════════════════════════════════════════════════════════
   2. DÉCISIONS À ARBITRER — tuile maîtresse (tout ce qui attend l'avocat)
   Données réelles, DEUX populations :
   • skill_jobs préparés (completed non validés — sémantique partagée
     cabHelpers, sans limite de temps) → bouton « Valider » 1-clic (RPC
     validate_skill_job), rendus AU-DESSUS des mails ;
   • incoming_emails préparés/urgents/à répondre (logique héritée de
     l'ex-MailsATraiterSection) → un clic ouvre Sophie dans le chat avec le
     contexte du mail (handler préservé).
   ════════════════════════════════════════════════════════════════════════ */
const PRO_CATEGORIES = ['client', 'juridiction', 'confrere', 'prestataire-pro'];

// Classification du correspondant → libellé HONNÊTE (qui écrit) + teinte.
// On n'affiche plus un nom de matière (Administratif/Affaires/Civil) à la place
// de la nature du correspondant : un mail du greffe = « Juridiction », pas
// « Administratif ». La teinte reste indicative.
const CLASSIF = {
  juridiction: { label: 'Juridiction', tone: cabMatiere('administratif') },
  confrere: { label: 'Confrère', tone: cabMatiere('affaires') },
  client: { label: 'Client', tone: cabMatiere('civil') },
  'prestataire-pro': { label: 'Prestataire', tone: cabMatiere('civil') },
};
const CLASSIF_DEFAULT = { label: 'Correspondant', tone: cabMatiere('civil') };

function DecisionsTile({ lawyerId, refreshKey, onValidated, onSelectEmail, onSeeAll }) {
  const [emails, setEmails] = useState(null);
  const [jobs, setJobs] = useState(null); // décisions préparées (skill_jobs) | null (chargement)

  // Décisions préparées par le cabinet : completed non validés, les plus
  // récents d'abord. L'embed client_dossiers(client_name) suit la FK
  // dossier_id → client_dossiers ; si PostgREST le refuse (cache de schéma,
  // FK renommée) on retombe sur la même requête SANS join plutôt que de
  // perdre les décisions. Si validated_at n'existe pas encore (migration 060
  // pas appliquée), les deux échouent → [] : la tuile redevient mails-only.
  // `refreshKey` (bump après validation réussie) refait le fetch : la liste
  // est plafonnée à 6 cartes, sans quoi valider les 6 viderait la tuile alors
  // que d'autres décisions attendent encore derrière.
  useEffect(() => {
    let active = true;
    (async () => {
      if (!lawyerId) { if (active) setJobs([]); return; }
      const query = (select) => supabase
        .from('skill_jobs')
        .select(select)
        .eq('lawyer_id', lawyerId)
        .eq('status', 'completed')
        .is('validated_at', null)
        .order('completed_at', { ascending: false })
        .limit(6);
      let { data, error } = await query('id, skill_slug, status, validated_at, completed_at, dossier_id, client_dossiers(client_name)');
      if (error) ({ data, error } = await query('id, skill_slug, status, validated_at, completed_at, dossier_id'));
      if (active) setJobs(error ? [] : (data || []));
    })().catch(() => { if (active) setJobs([]); });
    return () => { active = false; };
  }, [lawyerId, refreshKey]);

  // Validation 1-clic : retrait OPTIMISTE de la carte (la décision est
  // tranchée, elle n'a plus rien à faire sous les yeux de l'avocat), remise
  // en place si la base refuse (déjà validée ailleurs, job disparu…).
  // Garde anti double-clic : sans elle, la 2e RPC répond no-op (ok:false) et
  // remettrait dans la tuile une décision pourtant validée par la 1re.
  // Succès → onValidated() remonte au parent, qui fait recompter le hero et
  // recharger cette liste (décision suivante).
  const validatingIds = useRef(new Set());
  const handleValidateJob = async (job) => {
    if (validatingIds.current.has(job.id)) return;
    validatingIds.current.add(job.id);
    setJobs(prev => (prev || []).filter(j => j.id !== job.id));
    try {
      const res = await validateSkillJob(job.id);
      if (res.ok) {
        onValidated?.();
      } else {
        setJobs(prev => [...(prev || []), job].sort((a, b) => new Date(b.completed_at || 0) - new Date(a.completed_at || 0)));
      }
    } finally {
      validatingIds.current.delete(job.id);
    }
  };

  useEffect(() => {
    if (!lawyerId) { setEmails([]); return; }
    const since = new Date(Date.now() - 7 * 24 * 3600000).toISOString();
    supabase
      .from('incoming_emails')
      .select('id, from_email, from_name, subject, summary_ai, classification, urgency, action_required, dossier_id, received_at, is_read')
      .eq('lawyer_id', lawyerId)
      .in('classification', PRO_CATEGORIES)
      .or('urgency.eq.urgent,action_required.eq.reply,is_read.eq.false')
      .gte('received_at', since)
      .order('received_at', { ascending: false })
      .limit(12)
      .then(({ data }) => {
        const sorted = (data || []).sort((a, b) => {
          const score = e => (e.urgency === 'urgent' ? 100 : 0) + (e.action_required === 'reply' ? 50 : 0) + (!e.is_read ? 10 : 0);
          const d = score(b) - score(a);
          return d !== 0 ? d : new Date(b.received_at) - new Date(a.received_at);
        });
        setEmails(sorted.slice(0, 4));
      })
      .then(undefined, () => setEmails([]));
  }, [lawyerId]);

  const jobCount = jobs?.length || 0;
  const emailCount = emails?.length || 0;
  const count = jobCount + emailCount; // le badge compte TOUT ce qui attend l'avocat
  const loading = emails === null || jobs === null;

  return (
    <div style={{
      position: 'relative', borderRadius: K.rLg, overflow: 'hidden',
      background: `radial-gradient(120% 80% at 100% 0%, rgba(122,31,46,.06), transparent 58%), linear-gradient(180deg, var(--cab-card), ${K.cardSoft})`,
      boxShadow: K.lift3,
    }}>
      <div style={{ padding: '26px 28px 24px' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 14, marginBottom: 8 }}>
          <span style={{
            fontFamily: F.monoCab, fontSize: 10, letterSpacing: '.18em', textTransform: 'uppercase', color: '#fff',
            background: `linear-gradient(180deg, ${K.bordeauxHi}, ${K.bordeaux})`,
            padding: '5px 11px', borderRadius: 99, boxShadow: `0 4px 12px -4px ${K.bordeauxGlow}`,
          }}>À arbitrer maintenant</span>
          {count > 0 && (
            <span style={{
              fontFamily: F.monoCab, fontSize: 11, fontWeight: 600, color: K.bordeaux,
              background: K.bordeauxTint2, padding: '3px 10px', borderRadius: 99,
            }}>{count} {count > 1 ? 'décisions' : 'décision'}</span>
          )}
        </div>

        {/* Sous-texte uniquement en état vide — en présence de décisions, le hero
            juste au-dessus a déjà dit « préparé · il ne reste qu'à valider ». */}
        {count === 0 && !loading && (
          <p style={{ fontSize: 13.5, color: K.ink2, margin: '4px 0 18px', maxWidth: '52ch', lineHeight: 1.5 }}>
            Aucune décision en attente. <b style={{ color: K.ink, fontWeight: 600 }}>Le cabinet a tout traité</b> — vous serez alerté dès qu'un arbitrage sera requis.
          </p>
        )}

        {loading ? (
          <SkeletonRows />
        ) : count === 0 ? (
          <EmptyCalm icon={<Ic.check style={{ width: 18, height: 18 }} />} text="Boîte à arbitrages vide." />
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginTop: 14 }}>
            {/* Décisions préparées (skill_jobs) AU-DESSUS des mails : c'est du
                travail fini qui n'attend qu'un feu vert — plus actionnable
                qu'un mail à traiter. */}
            {jobs.map(job => <JobDecisionCard key={job.id} job={job} onValidate={handleValidateJob} />)}
            {emails.map(email => <DecisionCard key={email.id} email={email} onSelect={onSelectEmail} />)}
          </div>
        )}

        {/* Le lien cible la boîte mail du cabinet : on ne l'affiche que si des
            mails figurent dans la tuile (des jobs seuls n'y mènent pas). */}
        {emailCount > 0 && (
          <div style={{ marginTop: 16 }}>
            <LinkMore onClick={onSeeAll}>Voir toute la boîte du cabinet</LinkMore>
          </div>
        )}
      </div>
    </div>
  );
}

function DecisionCard({ email, onSelect }) {
  const [hover, setHover] = useState(false);
  const isMobile = useIsMobile();
  const c = CLASSIF[email.classification] || CLASSIF_DEFAULT;
  const tone = c.tone;
  const senderName = email.from_name || email.from_email?.split('@')[0] || 'Correspondant';
  const ctx = (email.summary_ai || '').substring(0, 180);
  const isUrgent = email.urgency === 'urgent';
  const needsReply = email.action_required === 'reply';

  return (
    <article
      onMouseEnter={() => setHover(true)} onMouseLeave={() => setHover(false)}
      style={{
        position: 'relative', borderRadius: 16, background: 'color-mix(in srgb, var(--cab-card) 72%, transparent)',
        border: `1px solid ${K.hair2}`, padding: '14px 18px 14px 20px',
        boxShadow: hover ? K.lift2 : K.lift1, overflow: 'hidden',
        transform: hover ? 'translateY(-1px)' : 'none',
        transition: 'box-shadow .3s var(--cab-ease), transform .3s var(--cab-ease)',
        // Disposition EN LIGNE : la largeur sert (avatar | objet+résumé | action).
        display: 'flex', alignItems: 'center', gap: 15, flexWrap: 'wrap',
      }}>
      <span aria-hidden="true" style={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: 4, background: tone.color }} />
      {/* Avatar correspondant */}
      <span style={{
        width: 40, height: 40, borderRadius: 11, flex: 'none',
        background: tone.soft, color: tone.color, display: 'grid', placeItems: 'center',
        fontFamily: F.serifDisplay, fontSize: 17, fontWeight: 600, border: `1px solid ${K.hair2}`,
      }}>{senderName.charAt(0).toUpperCase()}</span>

      {/* Milieu — étiquette + chips, objet, résumé (s'étire, tronque).
          minWidth bas : sur mobile l'avatar reste sur la même ligne que le contenu
          (le bouton, lui, passe pleine largeur dessous via flexWrap). */}
      <div style={{ flex: 1, minWidth: 140 }}>
        <div style={{
          fontFamily: F.monoCab, fontSize: 10, letterSpacing: '.08em', textTransform: 'uppercase',
          display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 4,
        }}>
          <span style={{ color: tone.color, fontWeight: 600 }}>{c.label}</span>
          <span style={{ color: K.ink3, fontWeight: 400, letterSpacing: '.04em' }}>· de {senderName}</span>
          {isUrgent && <Chip tone="bordeaux">Urgent</Chip>}
          {needsReply && <Chip tone="verified">À répondre</Chip>}
          {email.dossier_id && <Chip tone="ink">Dossier</Chip>}
        </div>
        {/* Objet : 1 ligne scannable en desktop ; sur mobile la carte est empilée,
            on laisse respirer sur 2 lignes plutôt que tronquer à ~20 caractères. */}
        <div style={{
          fontFamily: F.serifDisplay, fontWeight: 600, fontSize: 16.5, lineHeight: 1.25, letterSpacing: '-.01em', color: K.ink,
          overflow: 'hidden',
          ...(isMobile
            ? { display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }
            : { textOverflow: 'ellipsis', whiteSpace: 'nowrap' }),
        }}>
          {email.subject || '(réponse préparée)'}
        </div>
        {ctx && <div style={{ fontSize: 12.5, color: K.ink3, marginTop: 3, lineHeight: 1.45, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{ctx}</div>}
      </div>

      {/* Action à droite, centrée verticalement */}
      <button onClick={() => onSelect(email)} style={{
        flex: 'none', height: 40, padding: '0 17px', borderRadius: 11, border: 'none', cursor: 'pointer',
        fontWeight: 600, fontSize: 13, color: '#fff', fontFamily: F.sans,
        background: `linear-gradient(180deg, ${K.bordeauxHi}, ${K.bordeaux})`,
        display: 'inline-flex', alignItems: 'center', gap: 8,
        boxShadow: `0 8px 18px -8px ${K.bordeauxGlow}`,
      }}>
        <Ic.chat style={{ width: 15, height: 15 }} /> Traiter la réponse
      </button>
    </article>
  );
}

// Carte « décision préparée » (skill_job completed non validé) — même anatomie
// que DecisionCard (barre de teinte, avatar, milieu extensible, action à
// droite) pour que jobs et mails se lisent comme UNE liste de décisions.
// Le « Valider » est RÉEL : RPC validate_skill_job via onValidate (le parent
// retire la carte de façon optimiste).
function JobDecisionCard({ job, onValidate }) {
  const [hover, setHover] = useState(false);
  const isMobile = useIsMobile();
  const agent = agentForSlug(job.skill_slug);
  const tone = cabMatiere(categoryToMatiere(agent?.category));
  const label = humanizeSlug(job.skill_slug);
  const clientName = job.client_dossiers?.client_name || null;
  const ctx = [
    agent ? `Préparé${agent.gender === 'F' ? 'e' : ''} par ${agent.name}` : 'Préparé par le cabinet',
    job.completed_at ? fmtWhen(job.completed_at) : null,
  ].filter(Boolean).join(' · ');

  return (
    <article
      onMouseEnter={() => setHover(true)} onMouseLeave={() => setHover(false)}
      style={{
        position: 'relative', borderRadius: 16, background: 'color-mix(in srgb, var(--cab-card) 72%, transparent)',
        border: `1px solid ${K.hair2}`, padding: '14px 18px 14px 20px',
        boxShadow: hover ? K.lift2 : K.lift1, overflow: 'hidden',
        transform: hover ? 'translateY(-1px)' : 'none',
        transition: 'box-shadow .3s var(--cab-ease), transform .3s var(--cab-ease)',
        display: 'flex', alignItems: 'center', gap: 15, flexWrap: 'wrap',
      }}>
      <span aria-hidden="true" style={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: 4, background: tone.color }} />
      {/* Avatar de l'agent producteur (visage si dispo, initiale sinon) */}
      {agent?.photoUrl ? (
        <img src={agent.photoUrl} alt="" loading="lazy" style={{
          width: 40, height: 40, borderRadius: 11, flex: 'none', objectFit: 'cover', objectPosition: 'center top',
          border: `1px solid ${K.hair2}`,
        }} />
      ) : (
        <span style={{
          width: 40, height: 40, borderRadius: 11, flex: 'none',
          background: tone.soft, color: tone.color, display: 'grid', placeItems: 'center',
          fontFamily: F.serifDisplay, fontSize: 17, fontWeight: 600, border: `1px solid ${K.hair2}`,
        }}>{(agent?.name || 'C').charAt(0).toUpperCase()}</span>
      )}

      <div style={{ flex: 1, minWidth: 140 }}>
        <div style={{
          fontFamily: F.monoCab, fontSize: 10, letterSpacing: '.08em', textTransform: 'uppercase',
          display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 4,
        }}>
          <span style={{ color: tone.color, fontWeight: 600 }}>Travail préparé</span>
          {agent && <span style={{ color: K.ink3, fontWeight: 400, letterSpacing: '.04em' }}>· par {agent.name}</span>}
          <Chip tone="bordeaux">À valider</Chip>
          {clientName && <Chip tone="ink">{clientName}</Chip>}
        </div>
        <div style={{
          fontFamily: F.serifDisplay, fontWeight: 600, fontSize: 16.5, lineHeight: 1.25, letterSpacing: '-.01em', color: K.ink,
          overflow: 'hidden',
          ...(isMobile
            ? { display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }
            : { textOverflow: 'ellipsis', whiteSpace: 'nowrap' }),
        }}>
          {label}
        </div>
        {ctx && <div style={{ fontSize: 12.5, color: K.ink3, marginTop: 3, lineHeight: 1.45, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{ctx}</div>}
      </div>

      {/* Validation 1-clic, à droite comme l'action des cartes mail */}
      <button onClick={() => onValidate(job)} aria-label={`Valider ${label.toLowerCase()}`} style={{
        flex: 'none', height: 40, padding: '0 17px', borderRadius: 11, border: 'none', cursor: 'pointer',
        fontWeight: 600, fontSize: 13, color: '#fff', fontFamily: F.sans,
        background: `linear-gradient(180deg, ${K.bordeauxHi}, ${K.bordeaux})`,
        display: 'inline-flex', alignItems: 'center', gap: 8,
        boxShadow: `0 8px 18px -8px ${K.bordeauxGlow}`,
      }}>
        <Ic.check style={{ width: 15, height: 15 }} /> Valider
      </button>
    </article>
  );
}

/* ════════════════════════════════════════════════════════════════════════
   3. CABINET VIVANT — qui travaille sur quoi, en direct (jobs réels)
   Données réelles : skill_jobs en cours (Realtime), comme la JobsList E.
   ════════════════════════════════════════════════════════════════════════ */
function CabinetVivantTile({ lawyerId }) {
  const [jobs, setJobs] = useState(null);

  useEffect(() => {
    if (!lawyerId) { setJobs([]); return; }
    let active = true;
    const load = () => supabase
      .from('skill_jobs')
      .select('id, skill_slug, status, current_step, total_steps, current_step_label, created_at')
      .eq('lawyer_id', lawyerId)
      .in('status', ['pending', 'running'])
      .order('created_at', { ascending: false })
      .limit(4)
      .then(({ data }) => { if (active) setJobs(data || []); })
      .then(undefined, () => { if (active) setJobs([]); });
    load();
    const channel = supabase
      .channel('cab-vivant')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'skill_jobs', filter: `lawyer_id=eq.${lawyerId}` }, () => load())
      .subscribe();
    return () => { active = false; supabase.removeChannel(channel); };
  }, [lawyerId]);

  const has = (jobs?.length || 0) > 0;

  return (
    <div style={{ borderRadius: K.rLg, overflow: 'hidden', background: `linear-gradient(180deg, var(--cab-card), ${K.cardSoft})`, boxShadow: K.lift2 }}>
      <div style={{ padding: '24px 26px' }}>
        <TileHead
          glyph={<Ic.team />} glyphTone="bordeaux" kicker="Cabinet vivant"
          aside={has
            ? <LiveTag />
            : <span style={{ fontFamily: F.monoCab, fontSize: 10, letterSpacing: '.05em', color: K.ink4 }}>AU REPOS</span>}
        />
        {jobs === null ? (
          <SkeletonRows rows={2} />
        ) : !has ? (
          <EmptyCalm icon={<Ic.check style={{ width: 18, height: 18 }} />} text="Personne ne travaille en ce moment — tout est livré." />
        ) : (
          jobs.map((job, i) => <Worker key={job.id} job={job} first={i === 0} />)
        )}
      </div>
    </div>
  );
}

function Worker({ job, first }) {
  const agent = agentForSlug(job.skill_slug);
  const tone = cabMatiere(categoryToMatiere(agent?.category));
  const label = skillTaskLabel(job.skill_slug);
  const isPending = job.status === 'pending';
  const statusTxt = isPending ? 'En file' : 'En cours';
  // Détail d'étape seulement s'il apporte de l'info : ni « En file · En cours »
  // contradictoire, ni doublon avec le statut. Pas de barre quand le job est en file.
  const step = (!isPending && job.current_step_label && job.current_step_label !== statusTxt) ? job.current_step_label : null;
  const pct = (!isPending && job.total_steps > 0) ? Math.round(((job.current_step || 0) / job.total_steps) * 100) : null;

  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 13, padding: '12px 0',
      borderTop: first ? 'none' : `1px solid ${K.hair}`,
    }}>
      <span style={{ position: 'relative', width: 42, height: 42, flex: 'none' }}>
        {agent?.photoUrl
          ? <img src={agent.photoUrl} alt={agent.name} loading="lazy" style={{ width: '100%', height: '100%', borderRadius: 12, objectFit: 'cover', objectPosition: 'center top', border: `1px solid ${K.hair2}` }} />
          : <span style={{ width: '100%', height: '100%', borderRadius: 12, background: tone.soft, color: tone.color, display: 'grid', placeItems: 'center', fontFamily: F.serifDisplay, fontWeight: 600, border: `1px solid ${K.hair2}` }}>{(agent?.name || 'C').charAt(0)}</span>}
        <span aria-hidden="true" style={{ position: 'absolute', left: 0, right: 0, bottom: -3, height: 3, borderRadius: 99, background: tone.color }} />
      </span>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 13.5, color: K.ink2, lineHeight: 1.35 }}>
          <b style={{ color: K.ink, fontWeight: 600 }}>{agent?.name || 'Le cabinet'}</b> {agent ? 'travaille sur' : 'exécute'} <span style={{ color: K.ink3 }}>{label}</span>
        </div>
        <div style={{ fontSize: 11.5, color: K.ink3, marginTop: 3, display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <span style={{ color: tone.color, fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: 6 }}>
            <span aria-hidden="true" style={{ width: 11, height: 11, borderRadius: '50%', border: '2px solid currentColor', borderTopColor: 'transparent', display: 'inline-block', animation: 'cabRot .9s linear infinite' }} />
            {statusTxt}
          </span>
          {step && (
            <>
              <span aria-hidden="true" style={{ width: 3, height: 3, borderRadius: '50%', background: K.ink4 }} />
              {step}
            </>
          )}
        </div>
        {pct !== null && (
          <div style={{ marginTop: 7, height: 4, borderRadius: 99, background: K.hair, overflow: 'hidden', maxWidth: 190 }}>
            <i style={{ display: 'block', height: '100%', width: `${pct}%`, borderRadius: 99, background: `linear-gradient(90deg, ${K.bordeaux}, ${K.bordeauxHi})` }} />
          </div>
        )}
      </div>
      <span style={{ flex: 'none', alignSelf: 'flex-start', fontFamily: F.monoCab, fontSize: 10, color: K.ink4, letterSpacing: '.04em' }}>
        {pct !== null ? `${pct} %` : ''}
      </span>
    </div>
  );
}

/* ════════════════════════════════════════════════════════════════════════
   ÉCHÉANCES — agenda réel (composant AgendaUpcoming réutilisé tel quel)
   ════════════════════════════════════════════════════════════════════════ */
function EcheancesTile({ lawyerId, onSeeAll }) {
  return (
    <div style={{ borderRadius: K.rLg, overflow: 'hidden', background: 'linear-gradient(180deg, var(--cab-card), var(--cab-civil-soft))', boxShadow: K.lift2 }}>
      <div style={{ padding: '24px 26px 18px' }}>
        <TileHead glyph={<Ic.cal />} glyphTone="civil" kicker="Échéances surveillées"
          aside={<LinkMore onClick={onSeeAll}>L'agenda</LinkMore>} />
      </div>
      {/* AgendaUpcoming gère son propre fetch (calendar_events) + états vides. */}
      <div style={{ paddingBottom: 8 }}>
        <AgendaUpcoming onNavigate={onSeeAll ? () => onSeeAll() : undefined} lawyerId={lawyerId} />
      </div>
    </div>
  );
}

/* ════════════════════════════════════════════════════════════════════════
   Primitives partagées « Le Cabinet »
   ════════════════════════════════════════════════════════════════════════ */
function TileHead({ glyph, glyphTone = 'bordeaux', kicker, aside }) {
  const toneColor = glyphTone === 'civil' ? 'var(--cab-civil)' : K.bordeaux;
  const toneBg = glyphTone === 'civil' ? 'var(--cab-civil-soft)' : K.bordeauxTint;
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 14, marginBottom: 18 }}>
      <span style={{ display: 'flex', alignItems: 'center', gap: 11, fontSize: 12.5, fontWeight: 600, letterSpacing: '.02em', color: K.ink2 }}>
        {glyph && (
          <span aria-hidden="true" style={{ width: 30, height: 30, borderRadius: 9, display: 'grid', placeItems: 'center', flex: 'none', background: toneBg, color: toneColor }}>
            <span style={{ width: 16, height: 16, display: 'grid' }}>{glyph}</span>
          </span>
        )}
        <span style={{ fontFamily: F.monoCab, fontSize: 10.5, letterSpacing: '.16em', textTransform: 'uppercase' }}>{kicker}</span>
      </span>
      {aside}
    </div>
  );
}

function LinkMore({ onClick, children }) {
  const [hover, setHover] = useState(false);
  return (
    <button onClick={onClick} onMouseEnter={() => setHover(true)} onMouseLeave={() => setHover(false)} style={{
      background: 'none', border: 'none', padding: 0, cursor: 'pointer', fontFamily: F.sans,
      fontSize: 12, fontWeight: 600, color: hover ? K.ink : K.ink3,
      display: 'inline-flex', alignItems: 'center', gap: hover ? 8 : 5, transition: 'color .2s, gap .2s',
    }}>
      {children}<Ic.chevron style={{ width: 13, height: 13 }} />
    </button>
  );
}

function LiveTag() {
  return (
    <span style={{
      fontSize: 10, fontWeight: 700, letterSpacing: '.05em', color: K.verified,
      background: 'var(--cab-secretariat-soft)', padding: '3px 9px 3px 8px', borderRadius: 99,
      display: 'inline-flex', alignItems: 'center', gap: 6,
    }}>
      <span aria-hidden="true" style={{ width: 6, height: 6, borderRadius: '50%', background: K.verified, animation: 'cabRipple 2s var(--cab-ease-soft) infinite' }} />
      EN DIRECT
    </span>
  );
}

function Chip({ tone, children }) {
  const map = {
    bordeaux: { c: K.bordeaux, bg: K.bordeauxTint2 },
    verified: { c: K.verified, bg: 'var(--cab-secretariat-soft)' },
    ink: { c: K.ink3, bg: K.hair },
  };
  const t = map[tone] || map.ink;
  return (
    <span style={{ fontSize: 10.5, fontWeight: 700, letterSpacing: '.03em', color: t.c, background: t.bg, padding: '3px 9px', borderRadius: 99, whiteSpace: 'nowrap' }}>
      {children}
    </span>
  );
}

function EmptyCalm({ icon, text }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 11, padding: '14px 0', color: K.ink3, fontSize: 13 }}>
      <span style={{ width: 26, height: 26, borderRadius: '50%', background: 'var(--cab-secretariat-soft)', color: K.verified, display: 'grid', placeItems: 'center', flex: 'none' }}>{icon}</span>
      {text}
    </div>
  );
}

function SkeletonRows({ rows = 3 }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }} aria-hidden="true">
      {Array.from({ length: rows }).map((_, i) => (
        <div key={i} style={{ height: 64, borderRadius: 14, background: 'color-mix(in srgb, var(--cab-card) 60%, transparent)', border: `1px solid ${K.hair}`, opacity: 0.7, animation: 'cabBlink 1.6s ease-in-out infinite' }} />
      ))}
    </div>
  );
}

/* ─── Helpers data ─── */
// Référent d'une proposition d'anticipation d'audience (cron scan-audiences) :
// matière du payload (dossier > mot-clé) → agentForMatiere, sinon heuristique
// titre, sinon hint suggested_agent_id posé par le cron. Peut renvoyer null
// (ex. délibéré sans dossier) → le chat part alors vers Claire (orchestratrice).
function referentForProposal(p) {
  const pl = p?.action_payload || {};
  return agentForMatiere(pl.matiere || matiereFromTitle(pl.event_title || ''))
    || (pl.suggested_agent_id && AGENTS.find(a => a.id === pl.suggested_agent_id))
    || null;
}

function categoryToMatiere(category) {
  // Les catégories agents recoupent en grande partie les matières dossiers.
  if (!category) return null;
  const map = { pi: 'pi', affaires: 'affaires', transversal: 'civil', general: 'civil' };
  return map[category] || category;
}
// (skillTaskLabel / agentForSlug vivent désormais dans components/cab/cabHelpers.)
